// src/components/RequireRole.js
// -----------------------------------------------------------------------------
// Garde d'accès par rôles qui NE tranche PAS tant que l'état auth n'est pas prêt.
// - Attend isLoading
// - Attend que `user` soit présent si on est authentifié
// - Si pas authentifié -> /authentication/sign-in
// - Si rôle non autorisé -> /403
//
// 🔎 Ajouts: logs de diagnostic (dev-only) pour voir l'ordre des décisions.
// -----------------------------------------------------------------------------

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { isRoleAllowed } from "context/roles";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

export default function RequireRole({ allowedRoles, children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Logs de contexte (dev)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[RequireRole] path=", location.pathname, {
        isLoading,
        isAuthenticated,
        userRole: user?.role,
        allowedRoles,
      });
    }
  }, [location.pathname, isLoading, isAuthenticated, user, allowedRoles]);

  // 1) Tant que l'auth charge, on affiche un loader
  if (isLoading) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[RequireRole] loading...");
    }
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress color="info" />
      </Box>
    );
  }

  // 2) Si non connecté → login
  if (!isAuthenticated) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[RequireRole] not authenticated → /authentication/sign-in");
    }
    return <Navigate to="/authentication/sign-in" replace />;
  }

  // 3) Auth OK mais user pas encore hydraté
  if (!user) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[RequireRole] user not yet hydrated, waiting...");
    }
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
        <CircularProgress color="info" />
      </Box>
    );
  }

  // 4) Aucune contrainte de rôle -> accès
  if (allowedRoles == null) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[RequireRole] no role constraint -> allow");
    }
    return children;
  }

  // 5) Rôle non autorisé -> 403 (+ logs debug en dev)
  const allowed = isRoleAllowed(user.role, allowedRoles);
  if (!allowed) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[RequireRole] Access denied", {
        userRole: user?.role,
        allowedRoles,
        user,
      });
      window.__DEBUG_LAST_ROLE__ = {
        at: new Date().toISOString(),
        from: "RequireRole",
        user,
        userRole: user?.role,
        allowedRoles,
      };
    }
    return <Navigate to="/403" replace />;
  }

  // ✅ Rôle autorisé
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[RequireRole] allowed -> render children");
  }
  return children;
}

RequireRole.propTypes = {
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node.isRequired,
};
